import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import EditorialButton from '../components/editorial/EditorialButton';
import { getMembershipReceipt } from '../api/membership/membershipApi';
import { getMembershipThankYouCopy } from '../service/paymentThankYou';

// Stripe return page for Membership 5787 enroll, public guest-seat checkout
// (SeatForm), and extra seats bought from My Account (AccountSeatsForm).
// `?context=` on return_url picks the copy — joining vs seats reserved —
// so a guest-only payer is never told they just became a member.
//
// Extra seats live in hh_seat_purchases, not membership_enrollments, so
// GET /api/membership/receipt would never find them. Those flows skip the
// receipt poll (see getMembershipThankYouCopy) and send the member to
// My Account instead.
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 6;

export default function MembershipThankYou() {
  const params = new URLSearchParams(window.location.search);
  const paymentIntentId = params.get('payment_intent');
  const redirectStatus = params.get('redirect_status');
  const copy = getMembershipThankYouCopy(params.get('context'));

  const [receipt, setReceipt] = useState(null); // { ready, amount, receiptPdfUrl, receiptHostedUrl }
  const [pollsRemaining, setPollsRemaining] = useState(copy.pollReceipt ? MAX_POLL_ATTEMPTS : 0);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!copy.pollReceipt || !paymentIntentId || redirectStatus !== 'succeeded') return undefined;

    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const result = await getMembershipReceipt(paymentIntentId);
        if (cancelled) return;
        setReceipt(result);
        if (result.ready || attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setPollsRemaining(0);
          return;
        }
        setPollsRemaining(MAX_POLL_ATTEMPTS - attemptsRef.current);
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        console.error('Could not check receipt status:', err.message);
        if (!cancelled) setPollsRemaining(0);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentId, redirectStatus, copy.pollReceipt]);

  const stillWaiting = copy.pollReceipt && pollsRemaining > 0 && !receipt?.ready;
  const paymentSucceeded = redirectStatus === 'succeeded';

  return (
    <EditorialPageShell title="Thank you" description="Your KBT payment was received.">
      <Box
        component="section"
        sx={{
          position: 'relative',
          background: 'var(--navy-900)',
          color: '#fff',
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          pt: { xs: 18, md: 22 },
          pb: { xs: 10, md: 14 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(7,6,26,0.72) 0%, rgba(7,6,26,0.92) 100%), url('/assets/images/kbt/shul-gathering.jpg') center/cover no-repeat",
            opacity: 0.6,
          }}
        />
        <Box sx={{ position: 'relative', maxWidth: 900, mx: 'auto', px: { xs: 3, md: 6 }, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: 'var(--teal-300)',
                mb: 4,
              }}
            >
              {copy.kicker}
            </Box>

            {paymentSucceeded ? (
              <>
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 'clamp(40px, 7vw, 90px)',
                    lineHeight: 0.98,
                    letterSpacing: '-0.018em',
                    color: '#fff',
                    mb: 3,
                  }}
                >
                  {copy.titleAccent ? (
                    <>
                      {copy.title}{' '}
                      <span style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--teal-300)' }}>
                        {copy.titleAccent}
                      </span>
                    </>
                  ) : (
                    copy.title
                  )}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'clamp(15px, 1.4vw, 18px)',
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.85)',
                    maxWidth: 620,
                    mx: 'auto',
                    mb: 5,
                  }}
                >
                  {copy.body}
                  {' '}If anything looks off, write to{' '}
                  <span style={{ color: 'var(--teal-300)' }}>office@kbtla.org</span>.
                </Box>

                <Stack spacing={2.5} alignItems="center">
                  {copy.pollReceipt && (
                    receipt?.ready ? (
                      <EditorialButton
                        variant="primary"
                        href={receipt.receiptPdfUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Download Receipt
                      </EditorialButton>
                    ) : stillWaiting ? (
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ opacity: 0.85 }}>
                        <CircularProgress size={18} sx={{ color: 'var(--teal-300)' }} />
                        <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 14 }}>
                          Preparing your receipt…
                        </Box>
                      </Stack>
                    ) : (
                      <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 14, opacity: 0.75 }}>
                        Your receipt is on its way to your email.
                      </Box>
                    )
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                    <EditorialButton variant="primary" href={copy.primaryHref}>
                      {copy.primaryLabel}
                    </EditorialButton>
                    {copy.secondaryHref && (
                      <EditorialButton variant="inverse" href={copy.secondaryHref}>
                        {copy.secondaryLabel}
                      </EditorialButton>
                    )}
                  </Stack>
                </Stack>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 'clamp(32px, 5.5vw, 60px)',
                    lineHeight: 1.05,
                    letterSpacing: '-0.018em',
                    color: '#fff',
                    mb: 3,
                  }}
                >
                  {redirectStatus === 'processing' ? 'Your payment is processing.' : "We couldn't confirm that payment."}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'clamp(15px, 1.4vw, 18px)',
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.85)',
                    maxWidth: 620,
                    mx: 'auto',
                    mb: 5,
                  }}
                >
                  {redirectStatus === 'processing'
                    ? "This can take a moment — you'll get an email confirmation once it clears."
                    : 'No charge went through. Please try again, or reach out if you keep seeing this.'}
                  {' '}
                  <span style={{ color: 'var(--teal-300)' }}>office@kbtla.org</span>.
                </Box>
                <EditorialButton variant="primary" href={copy.failHref}>
                  {copy.failLabel}
                </EditorialButton>
              </>
            )}
          </motion.div>
        </Box>
      </Box>
    </EditorialPageShell>
  );
}
